import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import clsx from 'clsx';
import {
    MdOutlineDescription,
    MdOutlineMenuBook,
    MdOutlineBarChart,
    MdOutlineDashboard,
    MdOutlineNotifications,
    MdOutlineAssignment,
    MdOutlineLink,
    MdOutlineLanguage,
    MdOutlineOpenInNew,
    MdOutlineInfo,
} from 'react-icons/md';
import { resolveSystemLink, trimBasePath, type ISystemLinkContext } from '@/utils/resolveSystemLink';
import type { loadSystemLinkTitle } from '@/services/loadSystemLinkTitle';
import { useSystemLinkTitle } from './useSystemLinkTitle';
import SystemLinkDetails from './SystemLinkDetails';
import { getSystemLinkContext } from './systemLinkContext';
import './SystemLink.scss';

export interface ISystemLinkProps {
    href: string;
    caption?: string;
    target?: string;
    variant?: 'compact' | 'field';
    disabled?: boolean;
    routerNavigation?: boolean;
    className?: string;
    linkContext?: ISystemLinkContext;
    loadTitle?: typeof loadSystemLinkTitle;
}

const icons = {
    document: MdOutlineDescription,
    dictionary: MdOutlineMenuBook,
    report: MdOutlineBarChart,
    dashboard: MdOutlineDashboard,
    notification: MdOutlineNotifications,
    tasks: MdOutlineAssignment,
    internal: MdOutlineLink,
    external: MdOutlineLanguage,
};

const SystemLink = ({
    href,
    caption = '',
    target = '_blank',
    variant = 'compact',
    disabled = false,
    routerNavigation = false,
    className,
    linkContext,
    loadTitle,
}: ISystemLinkProps) => {
    // Разбор адреса — несколько new URL; в таблице на тысячи ячеек он не должен повторяться
    // на каждом рендере строки.
    const link = useMemo(() => {
        const context = linkContext ?? getSystemLinkContext();
        const basePath = trimBasePath(context.basePath);
        const address =
            routerNavigation && basePath && href.startsWith('/') && !href.startsWith(`${basePath}/`)
                ? `${basePath}${href}`
                : href;
        return resolveSystemLink(address, caption, context);
    }, [href, caption, routerNavigation, linkContext]);
    const title = useSystemLinkTitle(disabled ? undefined : link?.lookup, loadTitle);
    // Сведения принадлежат адресу: смена href в той же ячейке закрывает их без эффекта.
    const [openedFor, setOpenedFor] = useState<string>();
    const opened = openedFor === href;
    const trigger = useRef<HTMLButtonElement>(null);
    const external = link?.kind === 'external';
    const fallback = link?.lookup && 'id' in link.lookup ? `${link.label} №${link.lookup.id}` : link?.title;
    const label =
        title || fallback || caption || href;
    const Icon = icons[link?.kind ?? 'internal'];
    const newTab = target === '_blank';
    let safeAddress = link?.href;
    if (!safeAddress && (/^(mailto:|tel:)/i.test(href) || href.trim().startsWith('#'))) safeAddress = href.trim();

    if (disabled || !safeAddress) {
        return (
            <span
                className={clsx(disabled ? 'system-link__disabled' : 'system-link__plain', className)}
                aria-disabled={disabled || undefined}
                title={disabled ? 'Переходы отключены в support preview' : 'Недопустимый адрес ссылки'}
            >
                {caption || href}
            </span>
        );
    }
    const contents = (
        <>
            <Icon className="system-link__icon" aria-hidden="true" />
            <span className="system-link__title">{label}</span>
            {newTab && variant === 'field' && <MdOutlineOpenInNew className="system-link__new-tab" aria-hidden="true" />}
            {newTab && <span className="system-link__sr-only">Откроется в новой вкладке</span>}
        </>
    );
    // В ячейке значка новой вкладки нет — о ней говорит подсказка.
    const anchorTitle = newTab && variant === 'compact' ? `${label} — откроется в новой вкладке` : label;
    const anchorProps = { className: 'system-link__anchor', target, rel: 'noopener noreferrer', title: anchorTitle };
    return (
        <span
            className={clsx('system-link', `system-link--${variant}`, className)}
            data-kind={link?.kind ?? 'internal'}
            data-open={opened || undefined}
            // click и dblclick не должны выбирать или открывать строку таблицы. mousedown не гасится:
            // по нему document-обработчики закрывают открытые меню и поповеры, а строки DataTable
            // сами пропускают нажатия по ссылкам и кнопкам.
            onClick={(event) => event.stopPropagation()}
            onDoubleClick={(event) => event.stopPropagation()}
        >
            <span className="system-link__row">
                {routerNavigation ? (
                    <Link {...anchorProps} to={href}>
                        {contents}
                    </Link>
                ) : (
                    <a {...anchorProps} href={safeAddress}>
                        {contents}
                    </a>
                )}
                <button
                    ref={trigger}
                    type="button"
                    className="system-link__info"
                    aria-label={`Сведения о ссылке: ${label}`}
                    aria-haspopup="dialog"
                    aria-expanded={opened}
                    title="Сведения о ссылке"
                    onClick={() => setOpenedFor(opened ? undefined : href)}
                >
                    <MdOutlineInfo size={16} aria-hidden="true" />
                </button>
                {opened && (
                    <SystemLinkDetails
                        reference={trigger}
                        title={label}
                        meta={`${link?.label ?? 'Ссылка'}${link?.detail && !external ? ` · ${link.detail}` : ''}`}
                        address={safeAddress}
                        newTab={newTab}
                        onClose={(returnFocus) => {
                            setOpenedFor(undefined);
                            if (returnFocus) trigger.current?.focus();
                        }}
                    />
                )}
            </span>
            {variant === 'field' && external && link?.detail !== label && (
                <span className="system-link__domain">{link?.detail}</span>
            )}
        </span>
    );
};

export default SystemLink;
